import { properCaseName } from './contactName.js';
// v1.5.0: Email template variable renderer
// v1.7.40 (QB v18): real, canonical frontend Template/Merge Engine — now a
// superset covering both the original dotted-namespace tokens AND the flat
// token set + legacy HubSpot aliases that src/lib/vars.js's substituteVars
// used to handle separately for Compose.jsx/BulkCheckInModal.jsx. Real,
// deliberate consolidation: one real frontend renderer instead of two,
// same real spirit as the serverless-side api/_lib/mergeEngine.js
// consolidation done the same session. substituteVars in vars.js is
// retired (see that file); DEFAULT_PARTNERS/TOKEN_DEFS/date helpers stay.
//
// Replaces {{variable}} tokens with values from the task/contact/partner context.
//
// Variable namespaces:
//   {{first_name}}, {{last_name}}, {{full_name}}, {{first_last}}, {{name}} (alias)
//   {{company}}, {{company_name}} (alias), {{job_title}}, {{title}} (alias), {{email}}, {{phone}}
//   {{contact.email}}, {{contact.company}}, {{contact.job_title}}, {{contact.discipline}}, {{contact.region_or_state}}
//   {{partner.name}}, {{partner.one_liner}}, {{partner.intro_paragraph}}, {{partner.discipline_pitch}}
//   {{partner.pain_question}}, {{partner.latest_release_or_result}}
//   {{partner_name}}, {{partner_email}}, {{tagline}}, {{website}} — flat partner aliases
//   {{sender_name}} — flat alias for ctx.sender.name / manual-compose ctx.sender_name
//   {{task.title}}
//   {{sender.signature}}, {{sender.name}}, {{sender.first_name}}
//   {{closing.block}} — real, unilateral "Regards, / Shane / Romans 12:11" block,
//     single source of truth via closing_block table (v1.7.39.178)
//
// Real, legacy HubSpot-style dotted tokens (e.g. "{{ contact.firstname }}") are
// also normalized to their canonical flat form — ported from vars.js.
//
// Falsy/missing values render as empty string (not 'undefined').

const LEGACY_ALIASES = {
  firstname: 'first_name',
  lastname: 'last_name',
  fullname: 'full_name',
  companyname: 'company',
  jobtitle: 'job_title',
  emailaddress: 'email',
};

export function renderTemplate(template, ctx) {
  if (!template) return '';
  const c = ctx.contact || {};
  const p = ctx.partner || {};
  const vp = ctx.valueProp || {}; // partner_value_props row
  const t = ctx.task || {};
  const s = ctx.sender || {};

  // Discipline-specific pitch lookup (falls back to first available)
  const disciplinePitch = (() => {
    const pitches = vp.discipline_pitches || {};
    const d = c.primary_discipline || c.discipline;
    if (d && pitches[d]) return pitches[d];
    // Fall back to first available pitch
    const firstKey = Object.keys(pitches)[0];
    if (firstKey) return pitches[firstKey];
    // v1.7.40.98 (Ledger #1041) -- partner-level fallback. Previously this
    // returned '' when no per-discipline pitch existed, so
    // {{partner.discipline_pitch}} silently rendered empty with no warning.
    // partners.discipline_pitch is now a real editable field in Partner
    // Profile and serves as the default when no discipline-specific
    // override is set.
    return p.discipline_pitch || '';
  })();

  const first = properCaseName(c.first_name || ''); // #2707: ALL CAPS / all lower names read in proper case; mixed case is left alone
  const last = properCaseName(c.last_name || '');

  const companyName = c.company || c.company_name || (c.companies && c.companies.name) || '';
  const jobTitle = c.job_title || c.title || '';

  const map = {
    first_name: first || 'there',
    last_name: last,
    full_name: [first, last].filter(Boolean).join(' '),
    first_last: [first, last].filter(Boolean).join(' '),
    name: [first, last].filter(Boolean).join(' '), // flat alias, real, from vars.js
    referred_by: c.referred_by || '',
    // Real, flat aliases (ported from vars.js substituteVars, retired same session)
    company: companyName,
    company_name: companyName,
    job_title: jobTitle,
    title: jobTitle,
    email: c.email || '',
    phone: c.mobile_phone || c.phone || c.work_phone || '', // mobile_phone is the primary phone (#2696)
    partner_name: p.name || '',
    partner_email: p.email || '',
    sender_name: s.name || ctx.sender_name || '',
    tagline: p.tagline || '',
    website: p.website || '',
    'contact.email': c.email || '',
    'contact.company': companyName,
    'contact.job_title': jobTitle,
    'contact.discipline': c.primary_discipline || c.discipline || '',
    'contact.region_or_state': c.region || c.state || c.location || (c.companies && (c.companies.state || c.companies.region)) || 'your area',
    'partner.name': p.name || '',
    'partner.one_liner': vp.one_liner || p.one_liner || '',
    'partner.intro_paragraph': vp.intro_paragraph || p.intro_paragraph || '',
    'partner.discipline_pitch': disciplinePitch,
    'partner.pain_question': p.pain_question || '',
    // v1.7.40.98 (Ledger #1041) -- real positioning tokens, so messaging
    // set once in Partner Profile actually reaches templates instead of
    // being retyped per email.
    'partner.differentiators': p.differentiators || '',
    'partner.proof_points': p.proof_points || '',
    'partner.objection_handling': p.objection_handling || '',
    'partner.tagline': p.tagline || '',
    'partner.latest_release_or_result': (ctx.latestRelease && (ctx.latestRelease.summary || ctx.latestRelease.talking_points)) || p.latest_release_or_result || '',
    'task.title': t.title || '',
    'sender.name': s.name || '',
    // #2447: bare first name for the sign-off line ("Shane"); the signature block below it carries the full name and title.
    'sender.first_name': s.first_name || String(s.name || ctx.sender_name || '').trim().split(/\s+/)[0].replace(/[,.;]+$/, ''),
    'sender.signature': s.signature || (s.name ? `Best,\n${s.name}` : ''),
    'closing.block': s.closingBlock || '',
  };

  // VOICE RULES tokens ({{greeting}}, {{week_line}}, {{closing_line}}) are worked out by the SERVER from the phrase table (api/voice-rules resolve, the same engine send-hello uses).
  // The caller passes the answer in ctx.voice. A token with no answer is left out CLEANLY (no stray space, comma or empty line), never printed as raw braces; the caller shows a warning
  // (see renderWithVoice in voiceTokens.js).
  const voice = ctx.voice || {};
  let blanked = false;
  const out = template.replace(/\{\{\s*([a-z0-9_.]+)\s*\}\}/gi, (match, rawKey) => {
    const lower = rawKey.toLowerCase();
    if (lower === 'greeting' || lower === 'week_line' || lower === 'closing_line') {
      const v = voice[lower];
      if (v) return v;
      blanked = true;
      return GAP; // a marker, tidied below so only the space AROUND the left-out token is touched
    }
    const key = LEGACY_ALIASES[lower] || rawKey;
    if (map[key] != null) return map[key];
    if (ctx.onUnknown) ctx.onUnknown(rawKey); // a merge field this renderer does not know (a typo): the caller can name it and hold the send
    return '';
  });
  return blanked ? tidyAfterBlank(out) : out;
}

// Where a left-out voice token was: drop it together with the space that only existed for it. A token alone on its line takes the line with it; at the start of a line or tag it takes the space after
// it; at the end of a line or before punctuation it takes the space before it; between two words one space stays. NOTHING else in the text is touched (a space after a tag, double spaces elsewhere).
const GAP = String.fromCharCode(0xe000); // marks where a voice token was left out
const GAP_ALONE = new RegExp('(^|\n)[ \t]*' + GAP + '[ \t]*(?:\n[ \t]*\n|\n|$)', 'g'); // the token's own paragraph goes, with one paragraph break
const GAP_ANY = new RegExp('([ \t]*)' + GAP + '([ \t]*)', 'g');
function tidyAfterBlank(t) {
  const alone = t.replace(GAP_ALONE, '$1');
  return alone.replace(GAP_ANY, (m, before, after, offset, str) => {
    const prev = str[offset - 1];
    const next = str[offset + m.length];
    if (offset === 0 || prev === '\n' || (prev === '>' && !before)) return ''; // at the start of a line, or right after a tag with no space before it
    if (next === undefined || next === '\n' || next === '<' || /[,.;:!?]/.test(next)) return '';
    return before || after ? ' ' : '';
  });
}

// Smart-suggest: given a task, return the best-matching template intent.
export function inferIntent(taskTitle) {
  if (!taskTitle) return 'intro';
  const t = taskTitle.toLowerCase();
  if (/\b(re-?engage|reengage|cold|wake|reconnect|long time)\b/.test(t)) return 'reengagement';
  if (/\b(quote|pricing|proposal|estimate)\b/.test(t)) return 'quote_followup';
  if (/\b(meeting|meet|call|demo|schedule|book|grab time)\b/.test(t)) return 'meeting_request';
  if (/\b(follow.?up|follow up|circle back|check in|nudge|bump)\b/.test(t)) return 'follow_up';
  return 'intro';
}
